
import { useIsMobile } from '@/hooks/use-mobile';

/**
 * Responsive spacing utilities for mobile-first design
 */
export function useResponsiveSpacing() {
  const isMobile = useIsMobile();
  
  return {
    // Container spacing
    container: isMobile ? 'px-4' : 'px-6 lg:px-8',
    containerTight: isMobile ? 'px-3' : 'px-4 md:px-6',
    
    // Section spacing
    section: isMobile ? 'py-8' : 'py-12 lg:py-16',
    sectionTight: isMobile ? 'py-6' : 'py-8 lg:py-12',
    
    // Component spacing
    componentGap: isMobile ? 'gap-4' : 'gap-6 lg:gap-8',
    
    // Text spacing
    textSpacing: isMobile ? 'space-y-4' : 'space-y-6',
    
    // Safe area handling
    safeArea: 'pt-safe-top pb-safe-bottom pl-safe-left pr-safe-right',
  };
}

/**
 * Touch target utilities for better mobile UX
 */
export function useResponsiveTouchTargets() {
  const isMobile = useIsMobile();
  
  return {
    // Button sizes
    button: isMobile ? 'min-h-[44px] px-4 py-2' : 'h-10 px-4 py-2',
    buttonLarge: isMobile ? 'min-h-[48px] px-6 py-3' : 'h-12 px-6 py-3',
    buttonSmall: isMobile ? 'min-h-[40px] px-3 py-2' : 'h-8 px-3 py-1',
    
    // Icon buttons
    iconButton: isMobile ? 'min-h-[44px] min-w-[44px] p-2' : 'h-10 w-10 p-2',
    iconButtonLarge: isMobile ? 'min-h-[48px] min-w-[48px] p-3' : 'h-12 w-12 p-3',
    
    // Touch feedback
    touchFeedback: 'active:scale-95 transition-transform duration-150 ease-out touch-action-manipulation',
    touchFeedbackSubtle: 'active:scale-98 transition-transform duration-100 ease-out touch-action-manipulation',
  };
}

/**
 * Navigation utilities for responsive design
 */
export function useResponsiveNavigation() {
  const isMobile = useIsMobile();
  
  return {
    // Navbar heights
    navbarHeight: isMobile ? 'h-14' : 'h-16 lg:h-18',
    
    // Navigation item spacing
    navItemSpacing: isMobile ? 'py-3 px-4' : 'py-2 px-3',
    
    // Menu positioning
    menuOffset: isMobile ? 'mt-2' : 'mt-1',
  };
}

/**
 * Typography utilities for mobile readability
 */
export function useResponsiveTypography() {
  const isMobile = useIsMobile();
  
  return {
    // Headings
    h1: isMobile ? 'text-2xl font-bold leading-tight' : 'text-4xl lg:text-5xl font-bold leading-tight',
    h2: isMobile ? 'text-xl font-semibold leading-tight' : 'text-3xl lg:text-4xl font-semibold leading-tight',
    h3: isMobile ? 'text-lg font-semibold leading-tight' : 'text-2xl lg:text-3xl font-semibold leading-tight',
    
    // Body text
    body: isMobile ? 'text-base leading-relaxed' : 'text-lg leading-relaxed',
    bodySmall: isMobile ? 'text-sm leading-relaxed' : 'text-base leading-relaxed',
    
    // Input text (minimum 16px for iOS)
    input: 'text-base',
    
    // Reading width
    maxWidth: isMobile ? 'max-w-full' : 'max-w-2xl lg:max-w-4xl',
  };
}

/**
 * Layout utilities for responsive design
 */
export function useResponsiveLayout() {
  const isMobile = useIsMobile();
  
  return {
    // Grid layouts
    grid1: 'grid grid-cols-1',
    grid2: isMobile ? 'grid grid-cols-1' : 'grid grid-cols-2',
    grid3: isMobile ? 'grid grid-cols-1' : 'grid grid-cols-2 lg:grid-cols-3',
    grid4: isMobile ? 'grid grid-cols-1' : 'grid grid-cols-2 lg:grid-cols-4',
    
    // Flexbox layouts
    flexStack: isMobile ? 'flex flex-col' : 'flex flex-row',
    flexCenter: 'flex items-center justify-center',
    
    // Card layouts
    cardPadding: isMobile ? 'p-4' : 'p-6',
    cardSpacing: isMobile ? 'space-y-4' : 'space-y-6',
  };
}

/**
 * Input utilities for mobile optimization
 */
export function useResponsiveInputs() {
  const isMobile = useIsMobile();
  
  return {
    // Input sizing
    input: isMobile ? 'min-h-[48px] px-4 py-3 text-base' : 'h-10 px-3 py-2 text-sm',
    textarea: isMobile ? 'min-h-[120px] p-4 text-base' : 'min-h-[80px] p-3 text-sm',
    
    // Select styling
    select: isMobile ? 'min-h-[48px] px-4 text-base' : 'h-10 px-3 text-sm',
  };
}

/**
 * Animation utilities for mobile performance
 */
export function useResponsiveAnimations() {
  const isMobile = useIsMobile();
  
  return {
    // Reduced animations on mobile for performance
    hover: isMobile ? '' : 'hover:scale-105 transition-transform duration-200',
    focus: 'focus:ring-2 focus:ring-primary focus:ring-offset-2',
    
    // Touch feedback
    tap: 'active:scale-95 transition-transform duration-150',
    tapSubtle: 'active:scale-98 transition-transform duration-100',
  };
}

/**
 * Accessibility utilities for touch and keyboard navigation
 */
export function useAccessibilityUtils() {
  return {
    // Focus management
    focusVisible: 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2',
    
    // Touch target size
    touchTarget: 'min-h-[44px] min-w-[44px]',
    
    // Screen reader support
    srOnly: 'sr-only',
    
    // ARIA helpers
    ariaLabel: (label: string) => ({ 'aria-label': label }),
    ariaExpanded: (expanded: boolean) => ({ 'aria-expanded': expanded }),
  };
}

/**
 * Performance utilities for mobile optimization
 */
export function usePerformanceUtils() {
  return {
    // GPU acceleration
    gpuAcceleration: 'transform-gpu',
    
    // Will-change optimization
    willChange: 'will-change-transform',
    
    // Scroll optimization
    scrollOptimization: 'overscroll-contain scroll-smooth',
  };
}
